import { useEffect } from "react"
import {
  NeuigkeitenSection,
  NeuigkeitenTicker,
} from "@/components/home/neuigkeiten-section"
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router"
import { PageContainer } from "@/components/page-container"
import { LegacyAdvisorHome } from "@/components/legacy-advisor-home"
import { AdvisorHomeHero, type HomeStats } from "@/components/home/advisor-home-hero"
import { LearningEssenceSection } from "@/components/home/learning-essence-section"
import { RecentActivitySection } from "@/components/home/recent-activity-section"
import { FavoritesSection } from "@/components/home/favorites-section"
import { ProductTrainingSection } from "@/components/home/product-training-section"
import { isStartLayoutKey, type StartLayoutKey } from "@/components/start-layouts"
import { useFavoriteEntries } from "@/lib/use-recent-entries"
import { MEDIATHEK_ENABLED } from "@/lib/feature-flags"
import { useAdvisorHomeData } from "@/lib/use-advisor-home-data"
import { useSessionContext } from "@/lib/session"
import { useEssenceEmpfehlung } from "@/lib/essence-empfehlung"
import { useCoach } from "@/lib/use-coach"
import { parseChatSearch } from "@/lib/chat-navigation"

// Temporary demo values until training statistics are connected to the API.
const MOCK_HOME_STATS: HomeStats = {
  weekly: { value: 2, target: 3 },
  streak: 4,
  minutes: 48,
}

function AdvisorHome() {
  const { role } = useSessionContext()
  const navigate = useNavigate()
  const { identity, recents } = useAdvisorHomeData()
  const favorites = useFavoriteEntries()
  const empfohlen = useEssenceEmpfehlung()
  const coach = useCoach()
  useEffect(() => {
    if (role === "coach") void navigate({ to: "/coach", replace: true })
    if (role === "manager") void navigate({ to: "/management", replace: true })
  }, [role, navigate])
  return (
    <>
      <AdvisorHomeHero
        identity={identity}
        stats={MOCK_HOME_STATS}
        onStart={(frage) => void navigate({ to: "/chat", search: { frage } })}
      />
      <PageContainer width="library" className="library-sections">
        <NeuigkeitenTicker className="rounded-md border" />
        <NeuigkeitenSection />
        <LearningEssenceSection />
        <RecentActivitySection
          entries={recents.entries}
          loading={recents.isLoading}
          error={recents.isError}
          onRetry={recents.retry}
          portrait={identity.portrait}
          empfohlen={empfohlen}
          coachName={coach?.name}
        />
        <FavoritesSection entries={favorites} portrait={identity.portrait} />
        <ProductTrainingSection />
      </PageContainer>
    </>
  )
}
export const Route = createFileRoute("/_app/")({
  // `layout` rides along on top of the shared chat search: it only picks a
  // start page draft on the legacy home and is meaningless to /chat.
  validateSearch: (
    search: Record<string, unknown>
  ): ReturnType<typeof parseChatSearch> & { layout?: StartLayoutKey } => ({
    ...parseChatSearch(search),
    layout: isStartLayoutKey(search.layout) ? search.layout : undefined,
  }),
  beforeLoad: ({ search }) => {
    if (MEDIATHEK_ENABLED && (search.gespraech || search.frage))
      throw redirect({ to: "/chat", search, replace: true })
  },
  component: MEDIATHEK_ENABLED ? AdvisorHome : LegacyAdvisorHome,
})
